import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  DeleteOutline,
  EditOutlined,
  EventAvailableOutlined,
  MedicalServicesOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import ResidentDialog from "./ResidentDialog";
import LogDialog from "./LogDialog";
import {
  useGetResidentsQuery,
  useAddResidentMutation,
  useUpdateResidentMutation,
  useDeleteResidentMutation,
  useGetLogsQuery,
  useAddLogMutation,
  useDeleteLogMutation,
} from "../../services/resident.service";
import { CATEGORY_LABELS, formatDate } from "../../common/residentCategories";

const ManageResidents = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const allowed =
    currentUser &&
    currentUser.roles &&
    (currentUser.roles.includes("ROLE_CHIEF") || currentUser.roles.includes("ROLE_ADMIN"));

  if (!allowed) {
    return (
      <Box m="1.5rem 2.5rem">
        <Header title="Manage Residents" />
        <Typography sx={{ mt: "1.5rem" }}>
          This page is only available to the chief and admins.
        </Typography>
      </Box>
    );
  }
  return <ManageResidentsBoard />;
};

const ManageResidentsBoard = () => {
  const theme = useTheme();

  const { data: residents, isLoading: loadingResidents } = useGetResidentsQuery();
  const [addResident, { isLoading: addingResident }] = useAddResidentMutation();
  const [updateResident, { isLoading: updatingResident }] = useUpdateResidentMutation();
  const [deleteResident] = useDeleteResidentMutation();

  const [logFilter, setLogFilter] = useState("");
  const { data: logs, isFetching: loadingLogs } = useGetLogsQuery(logFilter);
  const [addLog, { isLoading: addingLog }] = useAddLogMutation();
  const [deleteLog] = useDeleteLogMutation();

  const [residentDialog, setResidentDialog] = useState({ open: false, resident: null });
  const [logDialog, setLogDialog] = useState({ open: false, resident: "", kind: "shift" });
  const [toDelete, setToDelete] = useState(null);
  const [notice, setNotice] = useState(null);

  const openLogDialog = (kind, resident = "") => setLogDialog({ open: true, resident, kind });

  const handleSaveResident = (values) => {
    const request = residentDialog.resident
      ? updateResident({ id: residentDialog.resident._id, ...values })
      : addResident(values);
    return request.unwrap().then(() => {
      setNotice({ severity: "success", text: residentDialog.resident ? "Resident updated." : "Resident added." });
    });
  };

  const handleSaveLog = (values) =>
    addLog(values)
      .unwrap()
      .then(() => {
        const name = residents.find((r) => r._id === values.resident)?.name;
        setNotice({ severity: "success", text: `Logged ${CATEGORY_LABELS[values.category]} for ${name}.` });
      });

  const handleConfirmDelete = () => {
    const { type, item } = toDelete;
    const request = type === "resident" ? deleteResident(item._id) : deleteLog(item._id);
    setToDelete(null);
    request
      .unwrap()
      .then(() => {
        if (type === "resident" && logFilter === item._id) {
          setLogFilter("");
        }
        setNotice({ severity: "success", text: type === "resident" ? "Resident removed." : "Entry removed." });
      })
      .catch((err) => setNotice({ severity: "error", text: err?.data?.message || "Delete failed." }));
  };

  const logColumns = [
    { field: "date", headerName: "Date", flex: 0.6, valueFormatter: ({ value }) => formatDate(value) },
    { field: "resident", headerName: "Ειδικευόμενος", flex: 1, valueGetter: ({ row }) => row.resident?.name },
    {
      field: "kind",
      headerName: "Type",
      flex: 0.6,
      valueGetter: ({ value }) => (value === "shift" ? "Εφημερία" : "Χειρουργείο"),
    },
    { field: "category", headerName: "Category", flex: 0.7, valueGetter: ({ value }) => CATEGORY_LABELS[value] },
    { field: "notes", headerName: "Notes", flex: 1.2 },
    { field: "loggedBy", headerName: "Logged by", flex: 0.7, valueGetter: ({ row }) => row.loggedBy?.username },
    {
      field: "createdAt",
      headerName: "Logged at",
      flex: 0.8,
      valueFormatter: ({ value }) => new Date(value).toLocaleString("el-GR"),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Delete entry">
          <IconButton size="small" onClick={() => setToDelete({ type: "log", item: row })}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const deleteText = !toDelete
    ? ""
    : toDelete.type === "resident"
    ? `Remove ${toDelete.item.name}? All shifts and surgeries logged for them will be deleted too.`
    : `Delete the ${CATEGORY_LABELS[toDelete.item.category]} entry of ${formatDate(toDelete.item.date)} for ${
        toDelete.item.resident?.name
      }?`;

  const paperSx = { p: "1.5rem", mt: "1.5rem", backgroundColor: theme.palette.background.alt };

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="Manage Residents" subtitle="Add residents and log their shifts and surgeries" />

      <Paper sx={paperSx}>
        <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap="0.75rem" mb="1rem">
          <Typography variant="h5" fontWeight={700}>
            Residents
          </Typography>
          <Box display="flex" flexWrap="wrap" gap="0.5rem">
            <Button variant="outlined" startIcon={<EventAvailableOutlined />} onClick={() => openLogDialog("shift")}>
              Log shift
            </Button>
            <Button variant="outlined" startIcon={<MedicalServicesOutlined />} onClick={() => openLogDialog("surgery")}>
              Log surgery
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => setResidentDialog({ open: true, resident: null })}
            >
              Add resident
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Ειδικευόμενος</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Τελική ειδικότητα</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loadingResidents && (residents || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>No residents yet.</TableCell>
                </TableRow>
              )}
              {(residents || []).map((resident) => (
                <TableRow key={resident._id} hover>
                  <TableCell>{resident.name}</TableCell>
                  <TableCell>{resident.specialty}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Log shift">
                      <IconButton size="small" onClick={() => openLogDialog("shift", resident._id)}>
                        <EventAvailableOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Log surgery">
                      <IconButton size="small" onClick={() => openLogDialog("surgery", resident._id)}>
                        <MedicalServicesOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setResidentDialog({ open: true, resident })}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => setToDelete({ type: "resident", item: resident })}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={paperSx}>
        <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap="0.75rem" mb="1rem">
          <Typography variant="h5" fontWeight={700}>
            Log history
          </Typography>
          <TextField
            select
            size="small"
            label="Resident"
            value={logFilter}
            onChange={(e) => setLogFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All residents</MenuItem>
            {(residents || []).map((r) => (
              <MenuItem key={r._id} value={r._id}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box
          height="60vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${theme.palette.divider}` },
          }}
        >
          <DataGrid
            loading={loadingLogs}
            getRowId={(row) => row._id}
            rows={logs || []}
            columns={logColumns}
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>

      <ResidentDialog
        open={residentDialog.open}
        resident={residentDialog.resident}
        onClose={() => setResidentDialog({ open: false, resident: null })}
        onSave={handleSaveResident}
        saving={addingResident || updatingResident}
      />
      <LogDialog
        open={logDialog.open}
        residents={residents}
        initialResident={logDialog.resident}
        initialKind={logDialog.kind}
        onClose={() => setLogDialog((state) => ({ ...state, open: false }))}
        onSave={handleSaveLog}
        saving={addingLog}
      />
      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)}>
        <DialogTitle>{toDelete?.type === "resident" ? "Remove resident?" : "Delete entry?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>{deleteText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">
            {notice.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
};

export default ManageResidents;
